/**
 * CADChatPanel - Lightweight ephemeral chat for CAD model analysis
 *
 * Creates a thread on first message, streams responses via SSE.
 * Resets on model change or unmount (no persistent history).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import CADChatComposer from './chat/CADChatComposer';
import CADChatMessages from './chat/CADChatMessages';
import { useCADChat } from './chat/useCADChat';
import { CADMarker } from '../../types/cad';

interface CADChatPanelProps {
  modelId: string | null;
  modelName?: string;
  onMarkersUpdate?: (markers: CADMarker[]) => void;
  comparisonModelId?: string;
  comparisonModelName?: string;
  onDirtyChange?: (dirty: boolean) => void;
}

const CADChatPanel: React.FC<CADChatPanelProps> = ({
  modelId,
  modelName,
  onMarkersUpdate,
  comparisonModelId,
  comparisonModelName,
  onDirtyChange,
}) => {
  const { t } = useTranslation();
  const { messages, input, setInput, loading, handleSend, handleKeyDown, messagesEndRef } =
    useCADChat({
      modelId,
      modelName,
      onMarkersUpdate,
      comparisonModelId,
      comparisonModelName,
      onDirtyChange,
    });

  // Stays below every hook: the panel is kept mounted across the parent's tab
  // switches, so the conversation has to survive this branch.
  if (!modelId) {
    return (
      <div className="p-4 text-center">
        <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
          {t('cad.chat.selectModel')}
        </p>
      </div>
    );
  }

  const sampleQuestions = [
    t('cad.chat.sampleSize'),
    t('cad.chat.sampleManufacturing'),
    t('cad.chat.sampleQuality'),
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <CADChatMessages
        messages={messages}
        guidance={t('cad.chat.guidance')}
        sampleQuestions={sampleQuestions}
        onSampleSelect={setInput}
        endRef={messagesEndRef}
      />
      <CADChatComposer
        value={input}
        onChange={setInput}
        onKeyDown={handleKeyDown}
        onSend={handleSend}
        loading={loading}
      />
    </div>
  );
};

export default CADChatPanel;
